/**
 * GAP3-12 (#227): the app landing page sends Android owners to the Apple App Store.
 *
 * /pages/ask-a-vet-app is the brand's flagship app page. Measured live on 2026-09-25: four
 * in-page calls to action, all reading "Download the App for FREE!", all pointing at
 * apps.apple.com. The only Google Play link on the page is a 117x40 badge in the footer, at the
 * very bottom of a page that is 60,000 px tall on desktop. Android is about half the Australian
 * market, and the primary market is Australia.
 *
 * WHY THIS IS A SCRIPT AND NOT AN EDIT TO THE PAGE. The better fix is a pair of store badges
 * beside each call to action, and that belongs in Replo. The page's markup does live in this
 * repository - snippets/reploChunk.785cee51-....1.liquid, 95 KB of it - but it is generated, and
 * the next publish from Replo overwrites the file wholesale. A hand-edit there is a fix with a
 * fuse on it. This runs beside whatever Replo publishes.
 *
 * WHAT IT DELIBERATELY DOES NOT TOUCH. The footer carries both store badges as images, and the
 * Apple one says "Download on the App Store" in its artwork. Sending an Android owner to Google
 * Play from a badge that reads App Store is a different kind of wrong, so only links that carry
 * TEXT and no image are rewritten - which on the live page is exactly the four calls to action.
 *
 * Desktop and iOS are left alone. A desktop visitor is not installing anything from the machine
 * they are reading on, and the harm this fixes is specific: an Android owner who taps the main
 * button and lands somewhere they cannot install from.
 */

export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.askavet.owner';

/**
 * Whether this user agent belongs to a device that installs from Google Play.
 *
 * Android tablets and phones both carry "Android". iPadOS reports itself as a Mac and carries
 * neither, which is correct here: an iPad installs from the App Store. "Windows Phone" is gone
 * but its user agent carried "Android" in some builds, so the Windows check comes first.
 *
 * @param {string} userAgent
 */
export const installsFromPlay = (userAgent) => {
  const ua = String(userAgent || '');
  if (/Windows/i.test(ua)) return false;
  return /Android/i.test(ua);
};

/**
 * The links worth rewriting: an App Store link the shopper reads as words rather than as a badge.
 *
 * Exported and pure so the rule can be tested without a DOM; the suite runs in node.
 *
 * @param {{href: string, text: string, imageCount: number}} link
 */
export const shouldRewrite = (link) => {
  if (!link || !/apps\.apple\.com/i.test(String(link.href || ''))) return false;
  if (link.imageCount > 0) return false;
  return String(link.text || '').trim().length > 0;
};

function rewriteAppleLinks(root, userAgent) {
  if (!installsFromPlay(userAgent)) return 0;

  let rewritten = 0;
  root.querySelectorAll('a[href*="apps.apple.com"]').forEach((anchor) => {
    const link = {
      href: anchor.getAttribute('href'),
      text: anchor.textContent,
      imageCount: anchor.querySelectorAll('img, svg, picture').length,
    };
    if (!shouldRewrite(link)) return;

    anchor.setAttribute('href', PLAY_STORE_URL);
    // So the change is visible to anyone debugging this in the field, and so the outbound exit
    // tracker (assets/woopf-outbound.js) records the store the shopper actually reached.
    anchor.setAttribute('data-woopf-store', 'google-play');
    rewritten += 1;
  });
  return rewritten;
}

if (typeof document !== 'undefined') {
  const run = () => rewriteAppleLinks(document, navigator.userAgent);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
}
